import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { guid } from '@datorama/akita';
import { Subscription } from 'rxjs';
import { CsBuildupState, SectionPartState, CsBuildupStateQuery, CsBuildupStateStore } from './editable-section-part.state';

@Component({ template: '' })
export abstract class EditableSectionPartComponent implements OnInit, OnDestroy {
  @Input() partOfSection: string;
  protected readonly id: string;
  private internalIsReadonly: boolean;
  private csBuildupStateSubscription: Subscription;

  public get isReadonly(): boolean {
    return this.internalIsReadonly;
  }

  constructor(
    protected element: ElementRef<Element>,
    protected csBuildupStore: CsBuildupStateStore,
    protected csBuildupStateQuery: CsBuildupStateQuery) {
    const name = element.nativeElement.getAttribute('name');
    this.id = name ?? guid();
  }

  ngOnInit(): void {
    this.partOfSection = this.partOfSection ?? this.element.nativeElement
      .closest('.cs-buildup-section')
      .getAttribute('name');
    this.initializeSectionPartState(this.partOfSection, this.id);
    this.csBuildupStateSubscription = this.csBuildupStateQuery
        .isSectionPartInEditMode(this.partOfSection, this.id)
        .subscribe(isInEditMode => {
          this.internalIsReadonly = !isInEditMode;
        });
  }

  ngOnDestroy(): void {
    this.csBuildupStateSubscription.unsubscribe();
    this.removeSectionPartState(this.partOfSection, this.id);
  }

  startEdit(): void {
    this.setSectionPartState(this.partOfSection, this.id, true);
  }

  endEdit(): void {
    this.setSectionPartState(this.partOfSection, this.id, false);
  }

  private initializeSectionPartState(sectionName: string, sectionPartName: string): void {
    this.csBuildupStore.update(state => {
      const isInEditMode = false;
      const sectionState = state.sections.find(section => section.name === sectionName);
      if (!sectionState) {
        state.sections.push({
          name: sectionName,
          parts: [{ name: sectionPartName, isInEditMode}]
        });
      } else {
        sectionState.parts.push({ name: sectionPartName, isInEditMode});
      }
    });
  }

  private removeSectionPartState(sectionName: string, sectionPartName: string): void {
    this.csBuildupStore.update(state => {
      const section = state.sections.find(s => s.name === sectionName);
      const partIndex = section.parts.findIndex(sectionPart => sectionPart.name === sectionPartName);
      section.parts.splice(partIndex, 1);
    });
  }

  private setSectionPartState(sectionName: string, sectionPartName: string, isInEditMode: boolean): void {
    this.csBuildupStore.update(state => {
      const sectionPartUiState = this.getSectionPartState(state, sectionName, sectionPartName);
      sectionPartUiState.isInEditMode = isInEditMode;
    });
  }

  private getSectionPartState(state: CsBuildupState, sectionName: string, sectionPartName: string): SectionPartState {
    return state.sections
      .find(section => section.name === sectionName)
      .parts
        .find(sectionPart => sectionPart.name === sectionPartName);
  }
}
